import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDownIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import LoadingSpinner from './LoadingSpinner';

export interface SearchableSelectOption {
  value: string;
  label: string;
  /** Extra text used when filtering (e.g. email) */
  keywords?: string;
}

interface SearchableSelectProps {
  id?: string;
  label?: string;
  value: string;
  onChange: (value: string) => void;
  options: SearchableSelectOption[];
  allOption?: SearchableSelectOption;
  searchPlaceholder?: string;
  disabled?: boolean;
  loading?: boolean;
  className?: string;
  emptyMessage?: string;
}

const MENU_MAX_HEIGHT = 320;

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  id,
  label,
  value,
  onChange,
  options,
  allOption = { value: '', label: 'All' },
  searchPlaceholder = 'Search…',
  disabled = false,
  loading = false,
  className = '',
  emptyMessage = 'No matches found',
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: 0, maxHeight: MENU_MAX_HEIGHT });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const selectedLabel = useMemo(() => {
    if (!value || value === allOption.value) return allOption.label;
    return options.find((o) => o.value === value)?.label ?? allOption.label;
  }, [value, options, allOption]);

  const filteredOptions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => {
      const haystack = `${o.label} ${o.keywords ?? ''}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [options, search]);

  const computeMenuPosition = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const width = Math.max(rect.width, 220);
    const left = Math.min(Math.max(12, rect.left), window.innerWidth - width - 12);
    const spaceBelow = window.innerHeight - rect.bottom - 12;
    const spaceAbove = rect.top - 12;
    const openUpward = spaceBelow < 240 && spaceAbove > spaceBelow;
    const maxHeight = Math.min(MENU_MAX_HEIGHT, openUpward ? spaceAbove - 8 : spaceBelow - 8);
    const top = openUpward ? Math.max(12, rect.top - maxHeight - 6) : rect.bottom + 6;

    setMenuPos({ top, left, width, maxHeight: Math.max(180, maxHeight) });
  };

  useEffect(() => {
    if (!open) return;
    setSearch('');
    computeMenuPosition();
    const timer = setTimeout(() => searchRef.current?.focus(), 50);
    return () => clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const onClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };

    const onScroll = (e: Event) => {
      const target = e.target as Node | null;
      if (menuRef.current?.contains(target) || listRef.current?.contains(target)) return;
      setOpen(false);
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };

    const onResize = () => computeMenuPosition();

    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('scroll', onScroll, true);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('scroll', onScroll, true);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', onResize);
    };
  }, [open]);

  const selectValue = (next: string) => {
    onChange(next);
    setOpen(false);
  };

  return (
    <div className={className}>
      {label && (
        <label htmlFor={id} className="block text-xs font-medium text-muted-foreground mb-1">
          {label}
        </label>
      )}
      <button
        ref={triggerRef}
        id={id}
        type="button"
        disabled={disabled || loading}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => {
          if (disabled || loading) return;
          setOpen((v) => !v);
        }}
        className="flex h-9 w-full min-w-[10rem] items-center justify-between gap-2 rounded-lg border border-border bg-background px-3 py-1.5 text-sm text-foreground shadow-sm transition-colors hover:border-primary/40 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <span className="truncate text-left">{loading ? 'Loading…' : selectedLabel}</span>
        {loading ? (
          <LoadingSpinner size="sm" />
        ) : (
          <ChevronDownIcon className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
        )}
      </button>

      {open &&
        createPortal(
          <>
            <div className="fixed inset-0 z-[9998]" aria-hidden onClick={() => setOpen(false)} />
            <div
              ref={menuRef}
              role="listbox"
              aria-label={label || 'Select option'}
              style={{
                position: 'fixed',
                top: menuPos.top,
                left: menuPos.left,
                width: menuPos.width,
                maxHeight: menuPos.maxHeight,
                zIndex: 9999,
              }}
              className="flex flex-col overflow-hidden rounded-lg border border-border bg-popover shadow-lg"
            >
              <div className="shrink-0 border-b border-border p-2">
                <div className="relative">
                  <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <input
                    ref={searchRef}
                    type="search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={searchPlaceholder}
                    className="h-8 w-full rounded-md border border-border bg-background pl-8 pr-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  />
                </div>
                {!search && options.length > 8 && (
                  <p className="mt-1.5 px-0.5 text-[11px] text-muted-foreground">
                    {options.length.toLocaleString()} options — type to search
                  </p>
                )}
              </div>

              <div ref={listRef} className="scroll-area min-h-0 flex-1 overflow-y-auto p-1">
                <button
                  type="button"
                  role="option"
                  aria-selected={!value || value === allOption.value}
                  onClick={() => selectValue(allOption.value)}
                  className={`flex w-full items-center rounded-md px-2.5 py-2 text-left text-sm transition-colors ${
                    !value || value === allOption.value
                      ? 'bg-primary/10 font-medium text-primary'
                      : 'text-foreground hover:bg-accent'
                  }`}
                >
                  {allOption.label}
                </button>

                {filteredOptions.length === 0 ? (
                  <p className="px-2.5 py-4 text-center text-sm text-muted-foreground">{emptyMessage}</p>
                ) : (
                  filteredOptions.map((option) => {
                    const isSelected = value === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        role="option"
                        aria-selected={isSelected}
                        onClick={() => selectValue(option.value)}
                        className={`flex w-full flex-col rounded-md px-2.5 py-2 text-left transition-colors ${
                          isSelected
                            ? 'bg-primary/10 font-medium text-primary'
                            : 'text-foreground hover:bg-accent'
                        }`}
                      >
                        <span className="truncate text-sm">{option.label}</span>
                        {option.keywords && (
                          <span className="truncate text-xs text-muted-foreground">{option.keywords}</span>
                        )}
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          </>,
          document.body
        )}
    </div>
  );
};

export default SearchableSelect;
